import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { Alert, Box, FormControl, Paper, TextField, Link } from '@mui/material';
import { ActionPriceList } from 'models/action';
import { LabClient } from 'models/client-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { useTranslation } from 'react-i18next';
import ActionListComponent from './action-list/action-list.component';
import ActionListPageActions from './action-list-page-actions/action-list-page-actions.component';
import { Link as RouterLink } from 'react-router-dom';

type AssociatedActionProps = {
    labClient: LabClient;
    actionPriceLists: ActionPriceList[];
    actionPriceListForClient: ActionPriceList;
    savedPriceList: ActionPriceList;
    isUsingDefault: boolean;
    onActionListChange: (actionList: ActionPriceList) => void;
    onUseDefault: () => void;
};
const AssociatedAction: FC<AssociatedActionProps> = ({
    labClient,
    onActionListChange,
    actionPriceLists,
    actionPriceListForClient,
    savedPriceList,
    isUsingDefault,
    onUseDefault,
}: AssociatedActionProps) => {
    const { t: translate } = useTranslation(['users', 'client-directory', 'orders']);
    const currentOrganization = currentOrgVar();
    const [actionPriceList, setActionPriceList] = useState<ActionPriceList>({} as ActionPriceList);
    const [filters, setFilters] = useState({ freetext: '', category: '' });
    const currency = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CURRENCY,
    );

    const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
    );

    useEffect(() => {
        if (actionPriceListForClient) {
            setActionPriceList(actionPriceListForClient);
        }
    }, [actionPriceListForClient, setActionPriceList]);

    const handleSelectPriceList = (actionList: ActionPriceList) => {
        // The dialog returns the base list (no client overrides). When the user
        // re-selects the list they currently have saved, use the saved/merged
        // version so their client prices are preserved.
        const listToApply = savedPriceList?._id && actionList._id === savedPriceList._id ? savedPriceList : actionList;
        setActionPriceList(listToApply);
        onActionListChange(listToApply);
    };

    const handleGeneralDiscountChange = (generalDiscount: string) => {
        if (parseInt(generalDiscount) > 100) generalDiscount = '100';
        const aPriceList = { ...actionPriceList, generalDiscount: generalDiscount };
        setActionPriceList(aPriceList);
        onActionListChange(aPriceList);
    };

    const handleDiscountedPriceChange = (actionDiscountedPrice: { id?: string; discountedPrice: string }) => {
        const clonedActions = [...(actionPriceList.actions || [])];
        const aPriceList = {
            ...actionPriceList,
            actions: clonedActions.map((action) =>
                action._id === actionDiscountedPrice.id
                    ? { ...action, discountedPrice: parseInt(actionDiscountedPrice.discountedPrice) }
                    : action,
            ),
        };
        setActionPriceList(aPriceList);
        onActionListChange(aPriceList);
    };

    const handleCustomerSpecificTiotandvardPriceChange = (actionTiotandvardPrice: {
        id?: string;
        customerSpecificTiotandvardPrice: string;
    }) => {
        const clonedActions = [...(actionPriceList.actions || [])];
        const aPriceList = {
            ...actionPriceList,
            actions: clonedActions.map((action) =>
                action._id === actionTiotandvardPrice.id
                    ? {
                          ...action,
                          customerSpecificTiotandvardPrice: parseInt(
                              actionTiotandvardPrice.customerSpecificTiotandvardPrice,
                          ),
                      }
                    : action,
            ),
        };
        setActionPriceList(aPriceList);
        onActionListChange(aPriceList);
    };

    return (
        <Fragment>
            {!labClient.customer.parentClinicId ? (
                <Fragment>
                    <ActionListPageActions
                        actionPriceList={actionPriceList}
                        isUsingDefault={isUsingDefault}
                        onSelectPriceList={handleSelectPriceList}
                        onUseDefault={onUseDefault}
                        onGeneralDiscountChange={handleGeneralDiscountChange}
                        actionPriceLists={actionPriceLists || []}
                        defaultActionPriceListId={currentOrganization.defaultActionPriceListId}
                    />
                    {!isUsingDefault && Object.keys(actionPriceList).length !== 0 && (
                        <Fragment>
                            <Box>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="freetext-filter"
                                        value={filters.freetext}
                                        label={translate('client-directory:ACTION_LIST_FILTER_LABEL')}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            setFilters({
                                                ...filters,
                                                freetext: event.target.value,
                                            })
                                        }
                                    />
                                </FormControl>
                            </Box>
                            <Box mt={2}>
                                <ActionListComponent
                                    actionPriceList={actionPriceList}
                                    onDiscountedPriceChange={handleDiscountedPriceChange}
                                    onCustomerSpecificTiotandvardPriceChange={
                                        handleCustomerSpecificTiotandvardPriceChange
                                    }
                                    filters={filters}
                                    currency={actionPriceList.currency || currency}
                                    isTioTandvardPricingForLabEnabled={isTioTandvardPricingForLabEnabled}
                                />
                            </Box>
                        </Fragment>
                    )}
                </Fragment>
            ) : (
                <Paper variant="outlined">
                    <Box p={2}>
                        <Alert severity="info">
                            {translate('client-directory:DISABLED_DUE_TO_PARENT')}
                            &nbsp;
                            {labClient.customer.parentClinicId && (
                                <Link
                                    component={RouterLink}
                                    to={`/client-directory/clients/${labClient.customer.parentClinicId}/price-list#action-list`}
                                >
                                    {translate('common:SHOW_CLINIC')}
                                </Link>
                            )}
                        </Alert>
                    </Box>
                </Paper>
            )}
        </Fragment>
    );
};

export default AssociatedAction;
